import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { message } from "antd";
import "./ApplePayButton.css";

const ApplePayButton = forwardRef(({ 
  validate,
  amount,
  onPaymentSuccess,
  addLog,
  publicKey,
  clientSecret,
  loading,
  setLoading
}, ref) => {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canMakePayment, setCanMakePayment] = useState(false);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(true);
  const [messageApi, contextHolder] = message.useMessage();

  // Helper function to check Apple Pay compatibility
  const checkApplePayCompatibility = () => {
    // Check if this is an Apple device
    const isAppleDevice = /iPad|iPhone|iPod|Macintosh/.test(navigator.userAgent) && !window.MSStream;
    
    // Check if this is Safari browser
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    // Check for ApplePaySession support (native Apple Pay API)
    const hasApplePayAPI = typeof window.ApplePaySession !== 'undefined' && 
                          window.ApplePaySession.canMakePayments();
    
    return isAppleDevice && (isSafari || hasApplePayAPI);
  };

  useEffect(() => {
    if (!stripe || !clientSecret) return;



    setIsCheckingAvailability(true);
    addLog('Checking Apple Pay availability...', 'info');

    // First check basic compatibility
    const isAppleDevice = /iPad|iPhone|iPod|Macintosh/.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    if (!isAppleDevice || !isSafari) {
      setIsCheckingAvailability(false);
      setCanMakePayment(false);
      addLog('Apple Pay not available (incompatible device/browser)', 'warning');
      return;
    }
    // const responce=validate()
    // console.log("responce",responce)
    // if (!responce) {
    //   setIsCheckingAvailability(false);
    //   setCanMakePayment(false);
    //   addLog('Apple Pay not available (validation failed)', 'warning');
    //   return;
    // }

    const pr = stripe.paymentRequest({
      country: 'GB',
      currency: 'gbp',
      total: {
        label: 'Total',
        amount: Math.round(parseFloat(amount) * 100),
      },
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: true,
      supportedPaymentMethods: [{
        supportedMethods: "https://apple.com/apple-pay",
        data: {
          version: 3,
          merchantIdentifier: "merchant.com.yourdomain",
          merchantCapabilities: ["supports3DS", "supportsCredit", "supportsDebit"],
          supportedNetworks: ["amex", "discover", "masterCard", "visa"],
          countryCode: "GB"
        }
      }]
    });

    pr.canMakePayment().then(result => {
      setIsCheckingAvailability(false);
      setCanMakePayment(!!result);
      
      if (result) {
        setPaymentRequest(pr);
        addLog('Apple Pay is available and ready', 'success');
        setLoading(false);
      } else {
        addLog('Apple Pay API available but no payment cards set up', 'warning');
        setLoading(false);
      }
    }).catch(error => {
      setIsCheckingAvailability(false);
      setCanMakePayment(false);
      addLog(`Apple Pay availability check failed: ${error.message}`, 'error');
    });

    pr.on('paymentmethod', async (ev) => {
      try {
        messageApi.loading('Processing payment...');
        addLog('Payment initiated via Apple Pay', 'info');
        
        const { error, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret,
          {
            payment_method: ev.paymentMethod.id
          },
          {
            handleActions: false
          }
        );

        if (error) {
          addLog(`Payment failed: ${error.message}`, 'error');
          ev.complete('fail');
          messageApi.error(error.message);
          return;
        }

        ev.complete('success');
        addLog('Payment succeeded', 'success');
        onPaymentSuccess(ev.paymentMethod.id);
        
      } catch (err) {
        addLog(`Payment error: ${err.message}`, 'error');
        ev.complete('fail');
        messageApi.error('Payment failed');
      }
    });

    return () => {
      if (pr) pr.off('paymentmethod');
    };
  }, [stripe, clientSecret, amount]);

  useImperativeHandle(ref, () => ({
    initiatePayment: () => {
      if (!paymentRequest) {
        messageApi.error('Apple Pay not initialized');
        setLoading(false);
        return false;
      }
      if (!canMakePayment) {
        messageApi.error('Apple Pay not available in this browser');
        setLoading(false);
        return false;
      }
      paymentRequest.show();
      setLoading(false);
      return true;
    },
    isReady: canMakePayment && !!paymentRequest
  }));



  // In ApplePayButton.js
if (loading || isCheckingAvailability) {
  return (
    <div className="ap-loading-container">
      <div className="ap-loading-card">
        <h3 className="ap-loading-title">Checking Apple Pay</h3>
        <p className="ap-loading-message">Verifying availability...</p>
      </div>
      {contextHolder}
    </div>
  );
}

if (!canMakePayment) {
  let warningMessage = 'Apple Pay requires an Apple device with Safari browser';
  
  // More specific error messages
  if (/iPad|iPhone|iPod|Macintosh/.test(navigator.userAgent)) {
    if (!/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
      warningMessage = 'Please use Safari browser to pay with Apple Pay';
    } else {
      warningMessage = 'Apple Pay is not set up or no supported cards available';
    }
  }

  return (
    <div className="ap-warning-container">
      <div className="ap-warning-card">
        <h3 className="ap-warning-title">Apple Pay Not Available</h3>
        <p className="ap-warning-message">{warningMessage}</p>
      </div>
      {contextHolder}
    </div>
  );
}

// If we get here, Apple Pay is available
return (
  <div className="ap-available-container">
    <div className="ap-available-card">
      <h3 className="ap-available-title">Apple Pay Available</h3>
      <p className="ap-available-message">
        You can pay with Apple Pay. Click the payment button to continue.
      </p>
    </div>
    {contextHolder}
  </div>
);
});

export default ApplePayButton;